import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import {
  NavigateNext,
  NavigateBefore,
  Send,
  Warning,
} from '@mui/icons-material';
import { quizAPI } from '../../services/api';
import axios from 'axios';
import QuestionCard from '../../components/Quiz/QuestionCard';
import QuizTimer from '../../components/Quiz/QuizTimer';
import QuizProgress from '../../components/Quiz/QuizProgress';
import toast from 'react-hot-toast';

const QuizTaking = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showTimeUpDialog, setShowTimeUpDialog] = useState(false);

  useEffect(() => {
    startQuizAttempt();
  }, [id]);

  const startQuizAttempt = async () => {
    try {
      setLoading(true);

      // Récupérer les détails du quiz
      const quizResponse = await quizAPI.getById(id);
      const quizData = quizResponse.data.quiz;
      setQuiz(quizData);

      // Démarrer une tentative
      const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:5001/api');
      const token = localStorage.getItem('accessToken');
      
      const attemptResponse = await axios.post(
        `${API_BASE_URL}/quiz/${id}/attempt`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setAttempt(attemptResponse.data.attempt);
      console.log('✅ Tentative démarrée:', attemptResponse.data.attempt);
    } catch (error) {
      console.error('Erreur lors du démarrage:', error);
      console.error('Détails erreur:', error.response?.data);
      
      if (error.response?.status === 400 && error.response?.data?.attemptId) {
        // Une tentative est déjà en cours - charger cette tentative
        toast('Reprise de votre tentative en cours...', { duration: 3000 });
        try {
          const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:5001/api');
          const token = localStorage.getItem('accessToken');
          
          const attemptResponse = await axios.get(
            `${API_BASE_URL}/attempt/${error.response.data.attemptId}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          
          setAttempt(attemptResponse.data.attempt);
          setQuiz(attemptResponse.data.attempt.quiz);
          
          // Charger les réponses sauvegardées
          if (attemptResponse.data.attempt.answers) {
            const savedAnswers = {};
            attemptResponse.data.attempt.answers.forEach((ans, idx) => {
              if (ans.selectedOptions && ans.selectedOptions.length > 0) {
                savedAnswers[idx] = ans.selectedOptions.length === 1 ? ans.selectedOptions[0] : ans.selectedOptions;
              } else if (ans.textAnswer) {
                savedAnswers[idx] = ans.textAnswer;
              }
            });
            setAnswers(savedAnswers);
          }
          
          toast.success('Tentative en cours chargée !');
          setLoading(false);
          return; // Ne pas naviguer ailleurs
        } catch (loadError) {
          console.error('Erreur lors du chargement de la tentative:', loadError);
          toast.error('Impossible de charger la tentative en cours');
          navigate('/quiz');
        }
      } else {
        const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Impossible de démarrer le quiz';
        toast.error(errorMsg);
        navigate('/quiz');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (answer) => {
    const questionId = quiz.questions[currentQuestionIndex]._id;
    
    setAnswers({
      ...answers,
      [currentQuestionIndex]: answer,
    });

    // Enregistrer la réponse sur le serveur
    saveAnswer(questionId, answer);
  };

  const saveAnswer = async (questionId, answer) => {
    if (!attempt) return;

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:5001/api');
      const token = localStorage.getItem('accessToken');

      await axios.post(
        `${API_BASE_URL}/attempt/${attempt._id}/answer`,
        {
          questionId,
          answer,
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('💾 Réponse sauvegardée');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la réponse:', error);
      // On ne bloque pas l'utilisateur, juste un warning
      toast.error('La réponse n\'a pas pu être sauvegardée', { duration: 2000 });
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = () => {
    const unansweredCount = quiz.questions.length - Object.keys(answers).length;
    
    if (unansweredCount > 0) {
      setShowSubmitDialog(true);
    } else {
      submitQuiz();
    }
  };

  const submitQuiz = async () => {
    try {
      setSubmitting(true);
      setShowSubmitDialog(false);

      const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:5001/api');
      const token = localStorage.getItem('accessToken');

      const response = await axios.post(
        `${API_BASE_URL}/attempt/${attempt._id}/submit`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('✅ Quiz soumis:', response.data);
      toast.success(response.data.message);
      
      // Rediriger vers les résultats
      navigate(`/quiz/${id}/results/${attempt._id}`);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTimeUp = () => {
    setShowTimeUpDialog(true);
    // Auto-submit après 5 secondes
    setTimeout(() => {
      submitQuiz();
    }, 5000);
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!quiz || !attempt) {
    return (
      <Container>
        <Alert severity="error">Quiz non trouvé</Alert>
      </Container>
    );
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === quiz.questions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {quiz.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {quiz.description}
        </Typography>

        {/* Timer */}
        {quiz.duration && attempt.sessionData?.startTime && (
          <Box sx={{ mt: 3 }}>
            <QuizTimer 
              durationMinutes={quiz.duration}
              startTime={attempt.sessionData.startTime}
              onTimeUp={handleTimeUp}
            />
          </Box>
        )}

        {/* Progress */}
        <Box sx={{ mt: 3 }}>
          <QuizProgress 
            currentQuestion={currentQuestionIndex}
            totalQuestions={quiz.questions.length}
            answeredQuestions={answers}
          />
        </Box>
      </Paper>

      {/* Question actuelle */}
      <QuestionCard
        question={currentQuestion}
        questionNumber={currentQuestionIndex + 1}
        totalQuestions={quiz.questions.length}
        answer={answers[currentQuestionIndex]}
        onAnswerChange={handleAnswerChange}
      />

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, mb: 5 }}>
        <Button
          variant="outlined"
          startIcon={<NavigateBefore />}
          onClick={handlePrevious}
          disabled={isFirstQuestion}
        >
          Précédent
        </Button>

        {isLastQuestion ? (
          <Button
            variant="contained"
            color="success"
            endIcon={<Send />}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Soumettre le Quiz'}
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<NavigateNext />}
            onClick={handleNext}
          >
            Suivant
          </Button>
        )}
      </Box>

      {/* Dialog de confirmation de soumission */}
      <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            Soumettre le quiz ?
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Vous n'avez pas répondu à toutes les questions ({quiz.questions.length - Object.keys(answers).length} question{quiz.questions.length - Object.keys(answers).length > 1 ? 's' : ''} non répondue{quiz.questions.length - Object.keys(answers).length > 1 ? 's' : ''}).
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Êtes-vous sûr de vouloir soumettre maintenant ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitDialog(false)}>
            Annuler
          </Button>
          <Button onClick={submitQuiz} variant="contained" color="warning">
            Soumettre quand même
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog temps écoulé */}
      <Dialog open={showTimeUpDialog} onClose={() => {}} disableEscapeKeyDown>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" />
            Temps écoulé !
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Le temps imparti pour ce quiz est écoulé. Votre quiz sera soumis automatiquement dans quelques secondes...
          </Typography>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default QuizTaking;

